import type { TargetTypeType } from '@terra/shared-schemas-orchestrator';
import { useCallback, useMemo } from 'react';
import {
  resolveActionDestination,
  resolveViewedEntityOnOpen,
} from '../../components/dashboard/notification-destinations';
import { useOpenHomeDestination } from '../../components/dashboard/useOpenHomeDestination';
import {
  useDismissTargetNotification,
  useMarkTargetEntityViewed,
  useTargetNotificationActions,
} from './useTargetNotificationCenter';

export function useNotificationActionsForHome({
  applicationId,
  targetType,
}: {
  applicationId: string;
  targetType: TargetTypeType;
}) {
  const target = useMemo(
    () => ({ targetId: applicationId, targetType }),
    [applicationId, targetType],
  );
  const { data, isLoading } = useTargetNotificationActions(target);
  const { mutate: dismissNotification } = useDismissTargetNotification(target);
  const { mutate: markEntityViewed } = useMarkTargetEntityViewed(target);
  const { openDestination } = useOpenHomeDestination({ applicationId });
  const actions = useMemo(() => data?.actions ?? [], [data?.actions]);

  const openAction = useCallback(
    (actionId: string) => {
      const action = actions.find((candidate) => candidate.id === actionId);
      const destination = action ? resolveActionDestination(action) : null;

      if (!action || !destination) {
        return;
      }

      openDestination(destination);

      const viewedEntity = resolveViewedEntityOnOpen(action);

      if (viewedEntity) {
        markEntityViewed(viewedEntity);
      }
    },
    [actions, markEntityViewed, openDestination],
  );

  const dismissAction = useCallback(
    (actionId: string) => dismissNotification({ notificationId: actionId }),
    [dismissNotification],
  );

  return { actions, isLoading, openAction, dismissAction };
}
